/**
 * Subdomain detection utilities for practice branding on login page.
 * Handles extraction of practice subdomain from URL for branded login experiences.
 */

// Subdomains that should be treated as root domain (no practice branding)
const RESERVED_SUBDOMAINS = ['www', 'app', 'api', 'admin', 'local', 'dev', 'staging'];

// Domains where we check for practice subdomains
const HEALIOSPACE_DOMAINS = ['healiospace.com', 'healio.space'];

/**
 * Extract the practice subdomain from the current URL.
 *
 * @example
 * - "smithpractice.healiospace.com" → "smithpractice"
 * - "app.healiospace.com" → undefined (reserved subdomain)
 * - "healiospace.com" → undefined (no subdomain)
 * - "localhost" → undefined (development)
 *
 * @param hostname - Optional hostname to parse (defaults to window.location.hostname)
 * @returns The practice subdomain, or undefined if not detected
 */
export const getSubdomainFromUrl = (hostname?: string): string | undefined => {
  const host = hostname ?? window.location.hostname;

  // Skip localhost and IP addresses
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return undefined;
  }

  const parts = host.toLowerCase().split('.');

  // Need at least 3 parts for a subdomain (e.g., practice.healiospace.com)
  if (parts.length < 3) {
    return undefined;
  }

  // Check if this is a HealioSpace domain
  const baseDomain = parts.slice(-2).join('.');

  if (!HEALIOSPACE_DOMAINS.includes(baseDomain)) {
    return undefined;
  }

  // Get the subdomain (everything before the base domain)
  const subdomain = parts.slice(0, -2).join('.');

  // Skip reserved subdomains
  if (!subdomain || RESERVED_SUBDOMAINS.includes(subdomain)) {
    return undefined;
  }

  return subdomain;
};

/**
 * Check if the current hostname is a branded practice subdomain.
 *
 * @param hostname - Optional hostname to check (defaults to window.location.hostname)
 * @returns true if a practice subdomain is detected
 */
export const isBrandedSubdomain = (hostname?: string): boolean => {
  return getSubdomainFromUrl(hostname) !== undefined;
};

/**
 * Check if we're on a development environment (localhost or dev subdomain).
 *
 * @param hostname - Optional hostname to check (defaults to window.location.hostname)
 * @returns true if on development environment
 */
export const isDevelopmentHost = (hostname?: string): boolean => {
  const host = hostname ?? window.location.hostname;

  return host === 'localhost' || host.startsWith('dev.') || host.startsWith('local.');
};
